// lib/claimSelf.ts — A8's three calls, in ONE place.
//
// B2 (2026-09-24): the walk found setup asking a new account for its name
// three times — the invite, setup's own name step, and then Today's
// ClaimYourself, because setup never created the performer; Today's card
// did. The fix is to create it in setup too, and the rule that comes with
// it is "reuse, don't fork": ClaimYourself's `submit` already IS this
// sequence (A8's header explains why it's three calls and in this order),
// so it is extracted here rather than re-typed a second time where the two
// copies could drift.
//
// getTeamMe(tenantId) -> createRecord(tenantId, AA.SUBJECT, {...}) ->
// bindMembershipParty(tenantId, membershipId, recordId) — in that order,
// exactly as A8 first wrote it. No retry, no swallowed step: a caller that
// wants ClaimYourself's granular per-step copy reads `ClaimSelfError.step`
// and writes its own sentence around `ClaimSelfError.message` (the
// underlying failure's own words); a caller that wants B2's simpler "stay
// put, retry the whole act" posture can just show `err.message`.
import { bindMembershipParty, createRecord, getTeamMe } from './api';
import { AA } from './auditionSpread';

export interface ClaimSelfInput {
  legalName: string;
  /** Omitted (never sent as an empty string) when blank — the pack's name
   *  template is `{professional_name|legal_name}` and an empty string is
   *  not absent, same rule ClaimYourself already followed. */
  professionalName?: string;
}

export interface ClaimSelfResult {
  recordId: string;
  membershipId: string;
  userId: string;
}

/** Which of the three calls failed, so a caller can show the right sentence
 *  without re-deriving the sequence. `'bind'` carries the record that DID
 *  get created — the record exists even though this step failed, which is
 *  why ClaimYourself's own bind-failure copy is a partial receipt, not a
 *  plain error. */
export type ClaimSelfStep = 'seat' | 'create' | 'bind';

export class ClaimSelfError extends Error {
  readonly step: ClaimSelfStep;
  readonly cause: unknown;
  readonly recordId?: string;

  constructor(step: ClaimSelfStep, cause: unknown, recordId?: string) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = 'ClaimSelfError';
    this.step = step;
    this.cause = cause;
    this.recordId = recordId;
  }
}

export async function claimSelf(tenantId: string, input: ClaimSelfInput): Promise<ClaimSelfResult> {
  const legalName = input.legalName.trim();
  const professionalName = input.professionalName?.trim();

  // ---- 1. who am I, here ----
  let membershipId: string;
  let userId: string;
  try {
    const me = await getTeamMe(tenantId);
    membershipId = me.member.id;
    userId = me.member.userId;
  } catch (err) {
    throw new ClaimSelfError('seat', err);
  }

  // ---- 2. the performer record, which is me ----
  let recordId: string;
  try {
    const record = await createRecord(tenantId, AA.SUBJECT, {
      legal_name: legalName,
      ...(professionalName ? { professional_name: professionalName } : {}),
      user_id: userId,
    });
    recordId = record.id;
  } catch (err) {
    throw new ClaimSelfError('create', err);
  }

  // ---- 3. bind my seat to it ----
  try {
    await bindMembershipParty(tenantId, membershipId, recordId);
  } catch (err) {
    throw new ClaimSelfError('bind', err, recordId);
  }

  return { recordId, membershipId, userId };
}
